/**
 * Production-ready reimagined Contribution History component.
 *
 * Renders inside the existing `slr.dashboard` route when the
 * `reimaginedAccountActivity` feature flag is enabled.
 *
 * Strips out: demo mode, DataIndicator badges, "Back to Finance" link,
 * links to reimagined-only routes (optimizer, etc).
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';
import { eq, or } from 'tio-ui/utilities';
import { VStack } from 'tio-ui/components/layout';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import ReSelect from 'tio-reimagined/components/select';
import Checkbox from 'tio-reimagined/components/checkbox';
import Chip from 'tio-reimagined/components/chip';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import { formatCurrency } from 'tio-reimagined/format';
import ContributionHero from 'tio-employee/components/reimagined/contribution-hero';
import AccountSelectorDrawer from 'tio-employee/components/reimagined/account-selector-drawer';
import PdfExportDrawer from 'tio-employee/components/reimagined/pdf-export-drawer';
import BenefitDetailDrawer from 'tio-employee/components/reimagined/benefit-detail-drawer';
import PaymentHistoryDrawer from 'tio-employee/components/reimagined/payment-history-drawer';
import type {
  ContributionHistoryData,
  ContributionPaymentRecord,
} from 'tio-employee/services/reimagined-data';
import { getPaymentYear } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type IntlService from 'ember-intl/services/intl';
import type AccountModel from 'tio-common/models/account';
import type PersonModel from 'tio-common/models/person';
import type PostalAddressModel from 'tio-common/models/postal-address';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';
import {
  PhBank,
  PhCaretUp,
  PhCaretDown,
  PhMoneyWavy,
  PhWarningCircle,
  PhLinkBreak,
  PhShieldSlash,
} from 'ember-phosphor-icons';
import InlineDocumentUpload from 'tio-employee/components/reimagined/inline-document-upload';
import DocumentUploadDrawer from 'tio-employee/components/reimagined/document-upload-drawer';
import {
  getActionItemIconBg,
  getActionItemIconColor,
  type ContributionActionItem,
} from 'tio-employee/services/reimagined-data';

// Server may include computed contribution fields not declared on AccountModel
type AccountWithContributions = AccountModel & {
  totalContributionAmount?: number;
  contributionCount?: number;
};

export interface ReimaginedContributionHistoryModel {
  contributionHistory: ContributionHistoryData;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  person: PersonModel;
}

interface ReimaginedContributionHistorySignature {
  Args: {
    model: ReimaginedContributionHistoryModel;
  };
}

export default class ReimaginedContributionHistory extends Component<ReimaginedContributionHistorySignature> {
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare store: StoreService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked isAccountDrawerOpen = false;
  @tracked isPdfDrawerOpen = false;
  @tracked isBenefitDrawerOpen = false;
  @tracked isPaymentHistoryDrawerOpen = false;
  @tracked isUploadDrawerOpen = false;
  @tracked selectedPaymentYear = '';
  @tracked showEditAddress = false;
  @tracked editAddressHasSubmitted = false;
  @tracked selectedAddress?: PostalAddressModel;
  @tracked addressConfirmationChecked = false;

  get history() {
    return this.args.model.contributionHistory;
  }

  get contributions() {
    return this.args.model.employerContributions.contributions;
  }

  get employerName() {
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      this.intl.t('slr.reimagined.your_employer')
    );
  }

  get person() {
    return this.args.model.person;
  }

  get activeAccounts() {
    return this.person.activeAccounts;
  }

  get accountReceivingContributions() {
    return this.person.accountReceivingContributions;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? '';
  }

  get isAddressSubmitDisabled() {
    return (
      !this.selectedAddress || this.editAddressHasSubmitted || !this.addressConfirmationChecked
    );
  }

  get selectedAddressId() {
    return this.selectedAddress?.id || '';
  }

  /**
   * User's first name for greeting
   */
  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName || this.intl.t('slr.reimagined.default_name')
    );
  }

  /**
   * Time-based greeting
   */
  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return this.intl.t('slr.reimagined.good_morning');
    if (hour < 17) return this.intl.t('slr.reimagined.good_afternoon');
    return this.intl.t('slr.reimagined.good_evening');
  }

  /**
   * Projected annual contribution
   */
  get projectedAnnual() {
    return this.contributions.projectedAnnual || 0;
  }

  /**
   * Benefit year to date
   */
  get benefitYearToDate() {
    return this.contributions.yearToDate || 0;
  }

  /**
   * Next contribution amount from the preview API.
   * Falls back to projected monthly if no preview data.
   */
  get nextContribution() {
    return this.history.nextPayment?.amount ?? this.monthlyContribution;
  }

  /**
   * Monthly contribution amount (projected from annual)
   */
  get monthlyContribution() {
    return Math.round(this.projectedAnnual / 12) || 0;
  }

  /**
   * Lifetime employer benefit
   */
  get lifetimeBenefit() {
    return this.contributions.lifetime || 0;
  }

  /**
   * Estimated remaining benefit for the year
   */
  get estimatedRemaining() {
    return Math.max(this.projectedAnnual - this.benefitYearToDate, 0);
  }

  /**
   * Benefit year range (start and end)
   */
  get benefitYearRange() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `10/01/${String(startYear).slice(2)}-10/01/${String(endYear).slice(2)}`;
  }

  /**
   * Benefit year progress percentage
   */
  get benefitYearProgressPercent() {
    if (this.projectedAnnual === 0) return 0;
    return Math.round((this.benefitYearToDate / this.projectedAnnual) * 100);
  }

  /**
   * Hero variant based on plan limits:
   * 1 = annual limit — tracker + 2 cards
   * 2 = lifetime limit — tracker + 3 cards
   * 3 = no limit — no tracker, 4 equal cards
   * 4 = both limits — tracker (annual) + 3 cards including lifetime
   */
  get heroVariant(): 1 | 2 | 3 | 4 {
    const annualLimit = this.plan?.annualLimit ?? 0;
    const lifetimeLimit = this.plan?.lifetimeLimit ?? 0;
    if (annualLimit > 0 && lifetimeLimit > 0) return 4;
    if (annualLimit > 0) return 1;
    if (lifetimeLimit > 0) return 2;
    return 3;
  }

  /**
   * The applicable limit amount in dollars for the hero tracker.
   * Uses annual limit (variant 1) or lifetime limit (variant 2).
   */
  get heroLimitAmount() {
    const annualLimit = this.plan?.annualLimit ?? 0;
    const lifetimeLimit = this.plan?.lifetimeLimit ?? 0;
    if (annualLimit > 0) return annualLimit / 100;
    if (lifetimeLimit > 0) return lifetimeLimit / 100;
    return 0;
  }

  /**
   * Lifetime limit in dollars (for variant 4 "both limits" — shown on Total Contribution card)
   */
  get lifetimeLimitAmount() {
    return (this.plan?.lifetimeLimit ?? 0) / 100;
  }

  /**
   * Annual limit in dollars
   */
  get annualLimitDollars() {
    return (this.plan?.annualLimit ?? 0) / 100;
  }

  /**
   * Progress percentage for annual usage against annual limit.
   */
  get annualProgressPercent() {
    if (this.annualLimitDollars <= 0) return 0;
    return Math.min(Math.round((this.benefitYearToDate / this.annualLimitDollars) * 100), 100);
  }

  /**
   * Progress percentage for lifetime usage against lifetime limit.
   */
  get lifetimeProgressPercent() {
    if (this.lifetimeLimitAmount <= 0) return 0;
    return Math.min(Math.round((this.lifetimeBenefit / this.lifetimeLimitAmount) * 100), 100);
  }

  /**
   * Whether the next contribution is blocked due to payment validation errors
   */
  get isBlocked() {
    return this.history.paymentValidationErrors.length > 0;
  }

  /**
   * Label for the hero tracker limit — "annual limit" or "lifetime limit"
   */
  get heroLimitLabel() {
    const annualLimit = this.plan?.annualLimit ?? 0;
    if (annualLimit > 0) return this.intl.t('slr.reimagined.annual_limit');
    return this.intl.t('slr.reimagined.lifetime_limit');
  }

  /**
   * Estimated remaining for the hero tracker based on which limit applies.
   */
  get heroEstimatedRemaining() {
    if (this.heroVariant === 1 || this.heroVariant === 4) {
      return Math.max(this.heroLimitAmount - this.benefitYearToDate, 0);
    }
    if (this.heroVariant === 2) {
      return Math.max(this.heroLimitAmount - this.lifetimeBenefit, 0);
    }
    return 0;
  }

  /**
   * Progress percentage for the hero tracker based on which limit applies.
   */
  get heroProgressPercent() {
    if (this.heroLimitAmount <= 0) return 0;
    const used =
      this.heroVariant === 1 || this.heroVariant === 4
        ? this.benefitYearToDate
        : this.lifetimeBenefit;
    return Math.min(Math.round((used / this.heroLimitAmount) * 100), 100);
  }

  /**
   * Formatted last payment date (e.g. "12/2025")
   */
  get lastPaymentFormatted() {
    const payment = this.history.lastPayment;
    if (!payment?.paidDate) return '';
    const date = new Date(payment.paidDate);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get lastPaymentAmount() {
    return this.history.lastPayment?.amount ?? 0;
  }

  /**
   * Current calendar year for the "{year} Contribution" label
   */
  get currentYear() {
    return new Date().getFullYear();
  }

  /**
   * Number of contribution payments made this benefit year
   */
  get paymentCount() {
    const currentYear = String(this.currentYear);
    const yearData = this.paymentsByYear?.find((y) => y.year === currentYear);
    return yearData?.paymentCount ?? 0;
  }

  /**
   * Formatted date of the earliest contribution for "as of" subtext.
   * Uses the last payment in the history (oldest chronologically).
   */
  get lifetimeAsOfDate() {
    const payments = this.history.payments;
    if (!payments?.length) return '';
    const oldest = payments[payments.length - 1];
    const dateStr = oldest?.paidDate;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  /**
   * Next expected contribution date from the preview API.
   * Falls back to first of next calendar month if no preview data.
   */
  get nextContributionDate() {
    const nextPayment = this.history.nextPayment;
    if (nextPayment?.benefitCycle) {
      const parts = nextPayment.benefitCycle.split('-');
      if (parts.length >= 2) {
        const yearPart = parts[0];
        const monthPart = parts[1];

        if (yearPart && monthPart) {
          const year = parseInt(yearPart, 10);
          const month = parseInt(monthPart, 10);

          if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
            const date = new Date(year, month - 1, 1);
            return this.intl.formatDate(date, { month: '2-digit', year: 'numeric' });
          }
        }
      }
    }
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return this.intl.formatDate(nextMonth, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get addressOptions() {
    return (
      // @ts-expect-error
      this.addressData.value?.map((address: PostalAddressModel) => ({
        label: htmlSafe(
          address.addressLine1 +
            (address.addressLine2 ? '\n ' + address.addressLine2 : '') +
            (address.addressLine3 ? '\n ' + address.addressLine3 : '')
        ),
        key: address.id,
        address,
      })) || []
    );
  }

  addressData = trackedFunction(this, async () => {
    // @ts-expect-error
    return await this.store.query('postal-address', {
      filter: {
        financialInstitution: this.person.accountReceivingContributions?.financialInstitution?.id,
        isActive: true,
        type: 'PAYMENT',
      },
      sort: 'address-line1',
      page: {
        offset: 0,
        limit: 400,
      },
    });
  });

  toggleEditAddress = () => {
    this.showEditAddress = !this.showEditAddress;
    this.selectedAddress = undefined;
    this.addressConfirmationChecked = false;
    this.editAddressHasSubmitted = false;
  };

  setSelectedAddress = (id: string | null) => {
    this.selectedAddress = this.addressOptions.find((a) => a.key === id)?.address;
  };

  updateAddress = () => {
    return this.updateAddressTask.perform();
  };

  updateAddressTask = dropTask(async () => {
    this.editAddressHasSubmitted = true;
    const account = this.person.accountReceivingContributions;
    if (!this.selectedAddress || !account) {
      this.editAddressHasSubmitted = false;
      return;
    }
    try {
      const newAddress = this.addressData.value?.find(
        // @ts-expect-error
        (a: PostalAddressModel) => a.id === this.selectedAddress?.id
      );
      if (!newAddress) {
        throw new Error('Address not found');
      }
      account.postalAddress = newAddress as PostalAddressModel;
      await account.save();
      this.toggleEditAddress();
      this.router.refresh('authenticated.slr.dashboard');
      const successMessage = this.intl.t('common.accounts.update_address.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      const errorMessage = this.intl.t('common.accounts.update_address.error');
      this.notifications.add(errorMessage, {
        appearance: 'error',
      });
    } finally {
      this.editAddressHasSubmitted = false;
    }
  });

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  toggleUploadDrawer = () => {
    this.isUploadDrawerOpen = !this.isUploadDrawerOpen;
  };

  togglePdfDrawer = () => {
    this.isPdfDrawerOpen = !this.isPdfDrawerOpen;
  };

  toggleBenefitDrawer = () => {
    this.isBenefitDrawerOpen = !this.isBenefitDrawerOpen;
  };

  openPaymentHistoryDrawer = (year: string) => {
    this.selectedPaymentYear = year;
    this.isPaymentHistoryDrawerOpen = true;
  };

  closePaymentHistoryDrawer = () => {
    this.isPaymentHistoryDrawerOpen = false;
  };

  get paymentsForSelectedYear(): ContributionPaymentRecord[] {
    if (!this.selectedPaymentYear) return [];
    return this.history.payments.filter((payment) => {
      if (payment.isFuture) return false;
      return getPaymentYear(payment) === this.selectedPaymentYear;
    });
  }

  get selectedYearTotal() {
    return this.paymentsForSelectedYear.reduce((sum, p) => sum + p.amount, 0);
  }

  get plan() {
    return this.sessionContext.currentEmployee?.plan ?? null;
  }

  onAccountChanged = () => {
    this.router.refresh('authenticated.slr.dashboard');
  };

  get receivingLoans() {
    return this.activeAccounts.filter(
      (account) => account.id === this.accountReceivingContributions?.id
    );
  }

  get otherLoans() {
    return this.activeAccounts.filter(
      (account) => account.id !== this.accountReceivingContributions?.id
    );
  }

  getAccountContributionAmount = (account: AccountWithContributions) => {
    return account.totalContributionAmount ?? 0;
  };

  getAccountContributionCount = (account: AccountWithContributions) => {
    return account.contributionCount ?? 0;
  };

  getAccountBalance = (account: AccountModel) => {
    return account.loanTotal;
  };

  getAccountLoanCount = (account: AccountModel) => {
    return account.summary?.loanCount ?? 0;
  };

  getAccountNumber = (account: AccountModel) => {
    return account.accountNumber || '';
  };

  getAccountNumberSuffix = (account: AccountModel) => {
    const num = account.accountNumber || '';
    return num.length >= 4 ? `...${num.slice(-4)}` : num;
  };

  getAccountLastUpdated = (account: AccountModel) => {
    const dateStr = account.providerSuccessAt;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    });
  };

  getAccountMinMonthlyPayment = (account: AccountModel) => {
    return account.loanMinMonthlyPayment;
  };

  getAccountPaymentAddress = (account: AccountModel) => {
    const address = account.postalAddress;
    if (!address) return '';
    const parts = [address.addressLine1];
    if (address.city || address.state || address.postalCode) {
      parts.push(
        [address.city, address.state].filter(Boolean).join(', ') +
          (address.postalCode ? ' ' + address.postalCode : '')
      );
    }
    return parts.filter(Boolean).join(' ');
  };

  get recentPayments() {
    return this.history.payments.filter((payment) => !payment.isFuture).slice(0, 3);
  }

  get lastPaymentIndex() {
    return this.recentPayments.length - 1;
  }

  getPaymentStatusLabel = (status: ContributionPaymentRecord['status']) => {
    if (status === 'sent') return this.intl.t('slr.reimagined.status_sent');
    if (status === 'in_progress') return this.intl.t('slr.reimagined.status_in_progress');
    if (status === 'resent') return this.intl.t('slr.reimagined.status_resent');
    if (status === 'failed') return this.intl.t('slr.reimagined.status_failed');
    return this.intl.t('slr.reimagined.status_pending');
  };

  getPaymentStatusIntent = (status: ContributionPaymentRecord['status']) => {
    if (status === 'sent') return 'success';
    if (status === 'in_progress') return 'primary';
    if (status === 'resent') return 'primary';
    if (status === 'failed') return 'danger';
    return 'default';
  };

  // Action Items - provided by service (demo or live)
  get actionItems() {
    return this.history.actionItems;
  }

  @tracked expandedActionItems = new Set<string>();

  toggleActionItem = (itemId: string) => {
    if (this.expandedActionItems.has(itemId)) {
      this.expandedActionItems.delete(itemId);
    } else {
      this.expandedActionItems.add(itemId);
    }
    this.expandedActionItems = new Set(this.expandedActionItems);
  };

  isActionItemExpanded = (itemId: string) => {
    return this.expandedActionItems.has(itemId);
  };

  getActionItemIconBg = (item: ContributionActionItem) => getActionItemIconBg(item);

  getActionItemIconColor = (item: ContributionActionItem) => getActionItemIconColor(item);

  // Payment History by Year - provided by service (demo or live)
  get paymentsByYear() {
    return this.history.paymentsByYear;
  }

  <template>
    <VStack class="w-full min-w-0">
      {{! Hero Banner - Student Loan Contributions }}
      <ContributionHero
        @greeting={{this.greeting}}
        @firstName={{this.firstName}}
        @variant={{this.heroVariant}}
        @annualUsage={{this.benefitYearToDate}}
        @annualLimit={{this.annualLimitDollars}}
        @annualProgressPercent={{this.annualProgressPercent}}
        @lifetimeUsage={{this.lifetimeBenefit}}
        @lifetimeLimit={{this.lifetimeLimitAmount}}
        @lifetimeProgressPercent={{this.lifetimeProgressPercent}}
        @lifetimeContribution={{this.lifetimeBenefit}}
        @lifetimeAsOfDate={{this.lifetimeAsOfDate}}
        @currentYear={{this.currentYear}}
        @yearContribution={{this.benefitYearToDate}}
        @paymentCount={{this.paymentCount}}
        @lastPaymentAmount={{this.lastPaymentAmount}}
        @lastPaymentDate={{this.lastPaymentFormatted}}
        @nextContribution={{this.nextContribution}}
        @nextContributionDate={{this.nextContributionDate}}
        @blocked={{this.isBlocked}}
        @onViewBenefit={{this.toggleBenefitDrawer}}
      />

      {{! Action Items Section }}
      {{#if this.actionItems.length}}
        <div class="mt-6 bg-gray-100 rounded-2xl p-4">
          <div class="flex items-center gap-2 px-1 mb-4">
            <Heading @level={{3}} class="text-lg font-semibold text-gray-800 leading-none">
              {{t "slr.reimagined.needs_your_attention"}}
            </Heading>
            <Text class="text-base font-medium text-gray-700">
              ({{this.actionItems.length}})
            </Text>
          </div>

          <div class="flex flex-col gap-2">
            {{#each this.actionItems as |item|}}
              <div class="bg-white border border-gray-200 rounded-2xl px-4 py-6">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div
                      class="flex items-center justify-center rounded-lg shrink-0 size-12
                        {{this.getActionItemIconBg item}}"
                    >
                      {{#if (eq item.issueType "account-setup")}}
                        <PhLinkBreak @size={{32}} class={{this.getActionItemIconColor item}} />
                      {{else if (eq item.issueType "plan-requirement")}}
                        <PhShieldSlash @size={{32}} class={{this.getActionItemIconColor item}} />
                      {{else}}
                        <PhWarningCircle @size={{32}} class={{this.getActionItemIconColor item}} />
                      {{/if}}
                    </div>
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2">
                        <Heading
                          @level={{4}}
                          class="text-base font-semibold text-gray-700 capitalize tracking-tight"
                        >
                          {{item.title}}
                        </Heading>
                        {{#if item.badge}}
                          <Chip @intent={{item.badgeIntent}} @size="sm">
                            {{item.badge}}
                          </Chip>
                        {{/if}}
                      </div>
                      <Text class="text-sm font-medium text-gray-500">
                        {{item.description}}
                      </Text>
                    </div>
                  </div>
                  <button
                    type="button"
                    {{on "click" (fn this.toggleActionItem item.id)}}
                    class="shrink-0 p-1"
                    aria-label={{if
                      (this.isActionItemExpanded item.id)
                      (t "slr.reimagined.collapse_action_item")
                      (t "slr.reimagined.expand_action_item")
                    }}
                  >
                    {{#if (this.isActionItemExpanded item.id)}}
                      <PhCaretUp @size={{24}} class="text-gray-600" />
                    {{else}}
                      <PhCaretDown @size={{24}} class="text-gray-600" />
                    {{/if}}
                  </button>
                </div>

                {{! Expandable Content Section }}
                {{#if (this.isActionItemExpanded item.id)}}
                  <div class="mt-6 bg-sky-50 rounded-2xl px-2 py-4">
                    <div class="px-5">
                      <InlineDocumentUpload @person={{this.person}} />
                    </div>
                  </div>
                {{/if}}
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{! Loan Accounts Section }}
      <div class="mt-10">
        {{! Top-level header with Edit and Update buttons }}
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center gap-2">
            <PhBank @size={{20}} class="text-gray-700" />
            <Heading @level={{2}} class="text-lg font-semibold text-gray-900 leading-none">
              {{t "slr.reimagined.loan_accounts"}}
            </Heading>
          </div>
          <button
            type="button"
            class="text-[#00809d] font-semibold text-base underline underline-offset-2 hover:text-[#006b82] transition-colors"
            {{on "click" this.toggleUploadDrawer}}
          >
            {{t "slr.reimagined.update_loan_information"}}
          </button>
        </div>

        {{! Grouped Loan Lists }}
        <div class="flex flex-col gap-8">
          {{! Receiving Contributions Section }}
          {{#if this.receivingLoans.length}}
            <div class="flex flex-col gap-4">
              <div class="flex items-center gap-2 text-base">
                <Heading @level={{3}} class="text-base font-semibold text-gray-800">
                  {{t "slr.reimagined.receiving_contributions"}}
                </Heading>
                <span class="text-gray-400 font-medium">|</span>
                <button
                  type="button"
                  aria-label={{t "slr.reimagined.change_receiving_account"}}
                  class="text-teal-600 font-medium underline decoration-solid hover:text-teal-700 cursor-pointer"
                  {{on "click" this.toggleAccountDrawer}}
                >
                  {{t "slr.reimagined.change"}}
                </button>
              </div>

              <div class="flex flex-col gap-3">
                {{#each this.receivingLoans as |account|}}
                  <div class="bg-white border border-gray-200 rounded-2xl p-4">
                    <div class="flex items-center justify-between">
                      <div class="flex flex-col gap-1.5">
                        <div class="flex items-center gap-2">
                          <Heading @level={{4}} class="text-base font-semibold text-gray-900">
                            {{account.financialInstitution.name}}
                            {{#if (this.getAccountNumber account)}}
                              <span class="text-gray-500 font-normal">
                                {{this.getAccountNumberSuffix account}}
                              </span>
                            {{/if}}
                          </Heading>
                          <Chip @intent="success" @size="sm">
                            {{t "slr.reimagined.active"}}
                          </Chip>
                        </div>
                        <div class="flex items-center gap-1.5">
                          <Text class="text-gray-500 text-sm font-medium">
                            {{formatCurrency (this.getAccountContributionAmount account)}}
                            {{t "slr.reimagined.received"}}
                          </Text>
                          <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                          <Text class="text-gray-500 text-sm font-medium">
                            {{this.getAccountContributionCount account}}
                            {{t
                              "slr.reimagined.contribution_count"
                              count=(this.getAccountContributionCount account)
                            }}
                          </Text>
                        </div>
                      </div>
                      <div class="flex flex-col items-end gap-1">
                        <Text class="text-base font-semibold text-gray-900">
                          {{formatCurrency (this.getAccountBalance account)}}
                        </Text>
                        {{#if (this.getAccountLastUpdated account)}}
                          <Text class="text-xs text-gray-400">
                            {{t
                              "slr.reimagined.as_of_date"
                              date=(this.getAccountLastUpdated account)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{! Other Loan Accounts Section }}
          {{#if this.otherLoans.length}}
            <div class="flex flex-col gap-4">
              <Heading @level={{3}} class="text-base font-semibold text-gray-800">
                {{t "slr.reimagined.other_loan_accounts"}}
              </Heading>

              <div class="flex flex-col gap-3">
                {{#each this.otherLoans as |account|}}
                  <div
                    class="bg-white border border-gray-200 rounded-xl px-4 py-5 flex items-center justify-between"
                  >
                    <div class="flex flex-col gap-1.5">
                      <div class="flex items-center gap-2">
                        <Heading @level={{4}} class="text-base font-semibold text-gray-900">
                          {{account.financialInstitution.name}}
                          <span class="text-gray-500 font-normal">
                            {{this.getAccountNumberSuffix account}}
                          </span>
                        </Heading>
                      </div>
                      <div class="flex items-center gap-1.5">
                        <Text class="text-gray-500 text-sm font-medium">
                          {{formatCurrency (this.getAccountContributionAmount account)}}
                          {{t "slr.reimagined.received"}}
                        </Text>
                        <div class="w-1 h-1 rounded-full bg-gray-400"></div>
                        <Text class="text-gray-500 text-sm font-medium">{{t
                            "slr.reimagined.no_contributions"
                          }}</Text>
                      </div>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                      <Text class="text-base font-semibold text-gray-900">
                        {{formatCurrency (this.getAccountBalance account)}}
                      </Text>
                      {{#if (this.getAccountLastUpdated account)}}
                        <Text class="text-xs text-gray-400">
                          {{t
                            "slr.reimagined.as_of_date"
                            date=(this.getAccountLastUpdated account)
                          }}
                        </Text>
                      {{/if}}
                    </div>
                  </div>
                {{/each}}
              </div>
            </div>
          {{/if}}

          {{! Empty state when no loan accounts }}
          {{#unless (or this.receivingLoans.length this.otherLoans.length)}}
            <div class="py-10 flex flex-col items-center justify-center gap-3 text-center">
              <Text class="text-sm text-gray-500">
                {{t "slr.reimagined.no_loan_accounts"}}
              </Text>
            </div>
          {{/unless}}
        </div>
      </div>

      {{! Payment History Section }}
      <div class="mt-10">
        {{! Section Header }}
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <PhMoneyWavy @size={{20}} class="text-gray-700" />
            <Heading @level={{2}} class="text-lg font-semibold text-gray-800 leading-none">
              {{t "slr.table.reimagined.payment_history"}}
            </Heading>
          </div>
          <button
            type="button"
            class="text-[#00809d] font-semibold text-base underline underline-offset-2 hover:text-[#006b82] transition-colors cursor-pointer"
            {{on "click" this.togglePdfDrawer}}
          >
            {{t "slr.table.reimagined.download_contribution_report"}}
          </button>
        </div>

        {{! Payment Cards Section }}
        {{#if this.paymentsByYear.length}}
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {{#each this.paymentsByYear as |yearData|}}
              <button
                type="button"
                class="bg-white border border-gray-300 rounded-2xl p-4 shadow-sm flex flex-col gap-8 text-left cursor-pointer hover:border-teal-400 hover:shadow-md transition-all"
                {{on "click" (fn this.openPaymentHistoryDrawer yearData.year)}}
                aria-label={{t "slr.reimagined.view_payment_history" year=yearData.year}}
              >
                <div class="flex flex-col gap-4">
                  <Text class="text-gray-500 text-base font-semibold">
                    {{t "slr.reimagined.year_contribution" year=yearData.year}}
                  </Text>
                  <Heading @level={{3}} class="text-gray-900 text-3xl font-semibold leading-none">
                    {{formatCurrency yearData.total}}
                  </Heading>
                </div>

                <div class="flex flex-col gap-4">
                  <div class="flex items-center gap-2">
                    <Text class="text-gray-500 text-sm font-medium">{{t
                        "slr.reimagined.account_label"
                        count=yearData.accounts.length
                      }}</Text>
                    <div class="flex items-center gap-1.5 flex-wrap">
                      {{#each yearData.accounts as |account index|}}
                        {{#if (eq index 0)}}
                          <div class="flex items-center gap-1.5">
                            <PhBank @size={{16}} class="text-gray-700" />
                            <Text class="text-gray-700 text-sm font-semibold">
                              {{account.name}}
                            </Text>
                          </div>
                        {{else}}
                          <Text class="text-gray-700 text-sm font-semibold">,</Text>
                          <div class="flex items-center gap-1.5">
                            <PhBank @size={{16}} class="text-gray-700" />
                            <Text class="text-gray-700 text-sm font-semibold">
                              {{account.name}}
                            </Text>
                          </div>
                        {{/if}}
                      {{/each}}
                    </div>
                  </div>

                  <div
                    class="flex items-center justify-between pt-2 border-t border-gray-200 text-xs"
                  >
                    <div class="flex items-center gap-1">
                      <PhMoneyWavy @size={{14}} class="text-teal-600" />
                      <Text class="text-gray-900 font-medium">
                        {{t "slr.reimagined.payment_count" count=yearData.paymentCount}}
                      </Text>
                    </div>

                  </div>
                </div>
              </button>
            {{/each}}
          </div>
        {{else}}
          <div class="py-10 flex flex-col items-center justify-center gap-2 text-center">
            <Text class="text-sm text-gray-500">{{t "slr.reimagined.no_payment_history"}}</Text>
          </div>
        {{/if}}
      </div>

      {{! Drawers }}
      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{this.activeAccounts}}
        @currentAccount={{this.accountReceivingContributions}}
        @personId={{this.person.id}}
        @onAccountChanged={{this.onAccountChanged}}
      />

      <BenefitDetailDrawer
        @isOpen={{this.isBenefitDrawerOpen}}
        @onClose={{this.toggleBenefitDrawer}}
        @plan={{this.plan}}
      />

      <PdfExportDrawer
        @isOpen={{this.isPdfDrawerOpen}}
        @onClose={{this.togglePdfDrawer}}
        @employeeId={{this.employeeId}}
      />

      <PaymentHistoryDrawer
        @isOpen={{this.isPaymentHistoryDrawerOpen}}
        @onClose={{this.closePaymentHistoryDrawer}}
        @year={{this.selectedPaymentYear}}
        @payments={{this.paymentsForSelectedYear}}
        @total={{this.selectedYearTotal}}
      />

      <DocumentUploadDrawer
        @isOpen={{this.isUploadDrawerOpen}}
        @onClose={{this.toggleUploadDrawer}}
        @person={{this.person}}
      />

      {{! Address Selection Modal }}
      <Modal @isOpen={{this.showEditAddress}} @onClose={{this.toggleEditAddress}} @size="xl">
        <:header>{{t "common.accounts.select_payment_address"}}</:header>
        <:body>
          <VStack class="p-0!">
            <p>{{t "slr.select_address_body_1"}}</p>
            <p class="italic text-gray-500">{{t "slr.select_address_body_2"}}</p>
            <form class="flex flex-col gap-y-4">
              <ReSelect
                @searchable={{true}}
                @clearable={{true}}
                @items={{this.addressOptions}}
                @selected={{this.selectedAddressId}}
                @onChange={{this.setSelectedAddress}}
                @label={{t "slr.reimagined.servicer_address"}}
              />
              <Checkbox
                @isDisabled={{this.updateAddressTask.isRunning}}
                @label={{t "slr.reimagined.address_confirmation"}}
                @checked={{this.addressConfirmationChecked}}
                @onChange={{fn (mut this.addressConfirmationChecked)}}
              />
            </form>
          </VStack>
        </:body>
        <:footer>
          <Footer
            @isRunning={{this.updateAddressTask.isRunning}}
            @onCancel={{this.toggleEditAddress}}
            @onSubmit={{this.updateAddress}}
            @submitDisabled={{this.isAddressSubmitDisabled}}
          />
        </:footer>
      </Modal>
    </VStack>
  </template>
}
